import { Card, EmptyState } from './Card.js';
import { fmtPct } from '../utils.js';
import { useMoney } from '../money.js';
import { PIE_COLORS } from '../chartTheme.js';
import type { RikuzLineRow } from '@agio/core';

/** Long thin stacked bar showing the sleeve split of the portfolio. */
function SplitBar({ lines }: { lines: RikuzLineRow[] }) {
  const withValue = lines.filter((l) => (l.value ?? 0) > 0);
  const total = withValue.reduce((a, l) => a + (l.value ?? 0), 0);
  if (!total) return null;
  return (
    <div>
      <div className="flex h-2.5 w-full overflow-hidden rounded-full">
        {withValue.map((l, i) => (
          <div
            key={l.report_id}
            title={`${l.sleeve_he} · ${fmtPct((l.value ?? 0) / total)}`}
            style={{ width: `${((l.value ?? 0) / total) * 100}%`, background: PIE_COLORS[i % PIE_COLORS.length] }}
          />
        ))}
      </div>
      <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1">
        {withValue.map((l, i) => (
          <span key={l.report_id} className="flex items-center gap-1.5 text-xs text-muted-foreground">
            <span className="h-2 w-2 rounded-full" style={{ background: PIE_COLORS[i % PIE_COLORS.length] }} />
            {l.sleeve_he}
          </span>
        ))}
      </div>
    </div>
  );
}

export const rikuzHasData = (lines: RikuzLineRow[]) => lines.length > 0;

/** The ריכוז table — sleeves as rows, value + % of portfolio (latest snapshot). */
export function RikuzTable({ lines }: { lines: RikuzLineRow[] }) {
  const money = useMoney();
  if (lines.length === 0) return <Card title="ריכוז"><EmptyState label="אין נתונים" /></Card>;
  const total = lines[0]?.total_value ?? null;
  const date = lines[0]?.report_date;
  const fundIds = lines.map((l) => l.fund_id);

  return (
    <Card title={`ריכוז${date ? ` · ${date}` : ''}`}>
      <SplitBar lines={lines} />
      <table className="print-data-table mt-4 w-full text-sm">
        <thead>
          <tr className="border-b border-border text-right text-muted-foreground">
            <th className="py-2 font-normal">אפיק</th>
            <th className="py-2 font-normal">שווי</th>
            <th className="py-2 font-normal">% מהתיק</th>
          </tr>
        </thead>
        <tbody>
          {lines.map((l) => (
            <tr key={l.report_id} className="border-b border-border/50">
              <td className="py-3">{l.sleeve_he}</td>
              <td className="py-3 tabular-nums">{l.value ? money(l.value, l.fund_id) : '--'}</td>
              <td className="py-3 tabular-nums text-muted-foreground">{l.value ? fmtPct(l.pct_of_total) : '--'}</td>
            </tr>
          ))}
          <tr className="font-semibold">
            <td className="py-3">סה"כ</td>
            <td className="py-3 tabular-nums text-success">{money(total, fundIds)}</td>
            <td className="py-3 tabular-nums">{fmtPct(1, 0)}</td>
          </tr>
        </tbody>
      </table>
    </Card>
  );
}
